/**
 * Shared read helpers for the structured care-log columns.
 *
 * Every reader tolerates null, a JSON string, or an already-parsed object:
 * Supabase returns jsonb as an object, logs written before the expansion
 * migration hold null, and hand-edited rows can arrive as text.
 *
 * They also tolerate two key spellings per field. The canonical names are the
 * ones the form now writes (`times_of_day`, `activity_type`, `woke_rested`,
 * `notes`); the earlier build wrote `times`, `activity`, `rested`, `detail`.
 * Reading both means rows saved either way still render, so no backfill is
 * needed.
 */

export const MEAL_KEYS = ['breakfast', 'lunch', 'dinner', 'snacks']

export const MEAL_LABEL = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snacks: 'Snacks',
}

export const MEAL_EMOJI = {
  breakfast: '🍳',
  lunch: '🥗',
  dinner: '🍽️',
  snacks: '🍎',
}

export const MEAL_STATUS = [
  { value: 'ate_well', label: 'Ate well', icon: '✓', short: '✓', tone: 'green' },
  { value: 'partial', label: 'Partial', icon: '~', short: '~', tone: 'amber' },
  { value: 'refused', label: 'Refused', icon: '✗', short: '✗', tone: 'danger' },
  { value: 'skipped', label: 'Skipped', icon: '–', short: '–', tone: 'grey' },
]

const STATUS_BY_VALUE = Object.fromEntries(MEAL_STATUS.map((s) => [s.value, s]))

export const GLASS_ML = 250
export const MAX_GLASSES = 12

export const SLEEP_QUALITY = [
  { value: 'poor', emoji: '😴', label: 'Poor' },
  { value: 'fair', emoji: '😐', label: 'Fair' },
  { value: 'good', emoji: '🙂', label: 'Good' },
  { value: 'great', emoji: '😊', label: 'Great' },
]

const QUALITY_BY_VALUE = Object.fromEntries(SLEEP_QUALITY.map((q) => [q.value, q]))

export function parseJson(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/** First key present with a non-null value — bridges the two key spellings. */
function pick(obj, ...keys) {
  if (!obj) return null
  for (const k of keys) {
    const v = obj[k]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return null
}

/** "morning" and "Morning" are both written in the wild. */
const titleCase = (s) =>
  typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s

/** Hours between a bedtime and wake time, rolling past midnight. */
export function sleepHours(bedtime, wakeTime) {
  if (!bedtime || !wakeTime) return null
  const [bh, bm] = String(bedtime).split(':').map(Number)
  const [wh, wm] = String(wakeTime).split(':').map(Number)
  if ([bh, bm, wh, wm].some((n) => Number.isNaN(n))) return null
  let mins = wh * 60 + wm - (bh * 60 + bm)
  if (mins <= 0) mins += 24 * 60 // slept through midnight
  return Math.round((mins / 60) * 10) / 10
}

/* ── Meals ─────────────────────────────────────────────────── */

/** Normalised per-meal entries, in canonical meal order. */
export function mealEntries(meals_json) {
  const meals = parseJson(meals_json)
  if (!meals) return []
  return MEAL_KEYS.filter((k) => meals[k] && (meals[k].status || pick(meals[k], 'notes', 'detail')))
    .map((k) => {
      const status = STATUS_BY_VALUE[meals[k].status] || null
      return {
        key: k,
        label: MEAL_LABEL[k],
        emoji: MEAL_EMOJI[k],
        status: meals[k].status || null,
        statusLabel: status?.label || meals[k].status || null,
        icon: status?.icon || null,
        tone: status?.tone || 'grey',
        detail: pick(meals[k], 'notes', 'detail'),
      }
    })
}

/** "Breakfast ✓ · Lunch ~ · Dinner ✓" */
export function mealsSummary(meals_json) {
  const entries = mealEntries(meals_json)
  if (!entries.length) return null
  return entries.map((e) => `${e.label} ${e.icon || e.statusLabel || ''}`.trim()).join(' · ')
}

/** Kept for the compact Tracking row. */
export function mealIcons(meals_json) {
  return mealEntries(meals_json).map((e) => ({
    key: e.key, label: e.label, icon: e.icon || '?', status: e.status,
  }))
}

/* ── Water ─────────────────────────────────────────────────── */

export function waterSummary(fluids_ml) {
  if (!fluids_ml) return null
  const glasses = Math.round(fluids_ml / GLASS_ML)
  return glasses > 0 ? `${glasses} glass${glasses === 1 ? '' : 'es'}` : `${fluids_ml}ml`
}

/* ── Movement ──────────────────────────────────────────────── */

export function movementParts(movement_json) {
  const m = parseJson(movement_json)
  if (!m) return null
  const rawTimes = pick(m, 'times_of_day', 'times')
  const times = Array.isArray(rawTimes) ? rawTimes.map(titleCase) : rawTimes ? [titleCase(rawTimes)] : []
  return {
    times,
    activity: titleCase(pick(m, 'activity_type', 'activity')),
    duration: pick(m, 'duration_min'),
    steps: pick(m, 'steps'),
    notes: pick(m, 'notes'),
  }
}

/** "Walk · 50 min · Morning" */
export function movementSummary(movement_json) {
  const p = movementParts(movement_json)
  if (!p) return null
  const bits = []
  if (p.activity) bits.push(p.activity)
  if (p.duration) bits.push(`${p.duration} min`)
  if (p.times.length) bits.push(p.times.join(', '))
  if (!bits.length && p.steps) bits.push(`${p.steps} steps`)
  if (!bits.length && p.notes) bits.push(p.notes)
  return bits.length ? bits.join(' · ') : null
}

/* ── Sleep ─────────────────────────────────────────────────── */

export function sleepParts(sleep_json) {
  const s = parseJson(sleep_json)
  if (!s) return null
  const bedtime = pick(s, 'bedtime')
  const wake = pick(s, 'wake_time')
  const q = QUALITY_BY_VALUE[s.quality]
  return {
    bedtime,
    wake,
    hours: s.hours ?? sleepHours(bedtime, wake),
    quality: s.quality || null,
    qualityLabel: q?.label || (s.quality ? titleCase(s.quality) : null),
    emoji: q?.emoji || null,
    rested: !!pick(s, 'woke_rested', 'rested'),
  }
}

/** "8.5h · Good · Rested" */
export function sleepSummary(sleep_json) {
  const p = sleepParts(sleep_json)
  if (!p) return null
  const bits = []
  if (p.hours != null) bits.push(`${p.hours}h`)
  if (p.qualityLabel) bits.push(p.qualityLabel)
  if (p.rested) bits.push('Rested')
  return bits.length ? bits.join(' · ') : null
}

export function sleepQualityEmoji(sleep_json) {
  return sleepParts(sleep_json)?.emoji || null
}

/* ── Supplements ───────────────────────────────────────────── */

export function supplementsList(supplements_json) {
  const list = parseJson(supplements_json)
  return Array.isArray(list) ? list.filter((s) => s?.name && String(s.name).trim()) : []
}

export function supplementsCount(supplements_json) {
  return supplementsList(supplements_json).length
}

/* ── Whole-log summary ─────────────────────────────────────── */

/** True if a log row carries any of the structured sections. */
export function hasAnyContent(log) {
  if (!log) return false
  return !!(
    mealEntries(log.meals_json).length ||
    log.fluids_ml ||
    movementSummary(log.movement_json) ||
    sleepSummary(log.sleep_json) ||
    supplementsCount(log.supplements_json) ||
    (log.meals_notes && String(log.meals_notes).trim())
  )
}

export function summarise(log) {
  if (!log) return {}
  return {
    meals: mealsSummary(log.meals_json),
    mealEntries: mealEntries(log.meals_json),
    mealIcons: mealIcons(log.meals_json),
    water: waterSummary(log.fluids_ml),
    movement: movementSummary(log.movement_json),
    sleep: sleepSummary(log.sleep_json),
    sleepEmoji: sleepQualityEmoji(log.sleep_json),
    supplements: supplementsCount(log.supplements_json),
  }
}
